<script lang="ts" setup>
import { computed } from 'vue'

import { Success } from '@/api/results'

import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'

import AppBreadcrumbs from '~/components/App/AppBreadcrumbs.vue'

const appStore = useAppStore()
const cityStore = useCityStore()
const route = useRoute()

const { isMobile, isTablet, isDesktop } = useDevice()

const { data: flatpageData } = await useAsyncData(async (nuxtApp) => {
  const result = await nuxtApp?.$apiService.site.fetchFlatpageData(route.path)

  if (result instanceof Success) {
    return result.data
  }
})

const flatpageContent = computed(() => flatpageData.value?.content || '')

const pageTitle = computed(() => flatpageData?.value?.title || 'Farfor')

const cityName = computed(() => cityStore.cityName)

const breadcrumbs = computed(() => {
  const pred = cityName.value
  const currentPath = flatpageData.value?.url || ''

  return [
    { text: `Доставка суши в ${pred}`, href: '/' },
    { text: pageTitle.value, href: currentPath },
  ]
})

const showBreadcrumb = computed(() => appStore.isWebview)

const flatpageClasses = computed(() => {
  return [
    'flatpage',
    {
      'flatpage--mobile': isMobile,
      'flatpage--tablet': isTablet,
    },
  ]
})

// Классы для блока с полученным HTML простой страницы
const htmlClasses = computed(() => {
  return [
    'flatpage__html',
    {
      'flatpage__html--mobile': isMobile,
      'flatpage__html--tablet': isTablet,
    },
  ]
})
</script>

<template>
  <div :class="flatpageClasses">
    <app-head
      :title="flatpageData?.seo_title"
      :seo="{
        title: flatpageData?.seo_title,
        description: flatpageData?.seo_description,
        keywords: flatpageData?.seo_keywords,
      }"
    />

    <div class="flatpage__wrapper">
      <app-breadcrumbs
        v-if="!showBreadcrumb"
        class="breadcrumbs-offset"
        :class="{ 'breadcrumbs-offset--desktop': isDesktop }"
        :server-breadcrumbs="breadcrumbs"
      />
      <div :class="htmlClasses" v-html="flatpageContent"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.flatpage {
  padding-top: 40px;

  &--tablet {
    padding-top: 24px !important;
    padding-bottom: 120px !important;

    :deep() {
      .breadcrumbs-mobile {
        .item {
          font-size: 16px;
        }
      }
    }
  }

  &--mobile {
    padding-top: 20px !important;
    padding-bottom: 120px !important;

    :deep() {
      .breadcrumbs-mobile {
        .item {
          font-size: 14px;
        }
      }
    }
  }

  &__wrapper {
    max-width: 960px;
    margin: 0 auto;
  }

  &__html:deep() {
    font-size: 14px;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    color: $text-black !important;

    h1 {
      margin-bottom: 48px;
      font-size: 28px !important;
    }

    h2,
    h3 {
      margin: 30px 0;
      font-size: 16px !important;
    }

    .v-h2-big {
      font-size: 22px !important;
    }

    h1,
    h2,
    h3,
    b,
    strong {
      font-weight: 800 !important;
    }

    p {
      margin: 0;
      margin-top: 20px;
      font-size: 14px !important;
    }

    b {
      font-weight: 800;
    }

    ol {
      list-style: none;
      counter-reset: li;

      li::before {
        font-weight: 800 !important;
        content: counters(li, '.') '. ';
        counter-increment: li;
      }
    }

    ul {
      padding-left: 16px;
      list-style-type: disc;

      li {
        margin: 16px 0;
      }

      li:not(:last-child) {
        // margin-bottom: 10px;
      }

      li::marker {
        font-weight: 500 !important;
      }
    }

    // Иконка
    img {
      width: 24px;
      height: 24px;
      margin-right: 10px;
    }

    table {
      table-layout: fixed;
      border-collapse: collapse;

      thead {
        border-bottom: 1px solid $button-background;
      }

      tbody {
        tr:first-child {
          height: 10px;
        }
      }

      th {
        height: 48px;
        font-weight: 800;
      }

      td {
        height: 36px;
      }

      th,
      td {
        width: 100%;

        &:not(:last-child) {
          padding-right: 10px;
        }
        &:last-child {
          padding-left: 10px;
        }
      }
    }

    .v-table-important {
      width: 100%;
      max-width: 634px;

      th:first-child,
      td:first-child {
        text-align: left;
      }

      th:last-child,
      td:last-child {
        text-align: center;
      }
    }

    .v-margin-bottom-20 {
      margin-bottom: 20px !important;
    }

    .v-margin-bottom-30 {
      margin-bottom: 30px !important;
    }
    .v-margin-bottom-40 {
      margin-bottom: 40px !important;
    }

    .v-margin-y-20 {
      margin: 20px 0 !important;
    }

    .v-flex-container {
      display: flex;
      gap: 16px;
      align-items: center;
    }
  }

  &__html--tablet:deep() {
    font-size: 14px;

    h1 {
      margin-bottom: 24px !important;
      font-size: 24px !important;
    }

    h2,
    h3 {
      font-size: 16px !important;
    }

    .v-h2-big {
      font-size: 22px !important;
    }

    p {
      font-size: 14px !important;
    }
  }

  &__html--mobile:deep() {
    font-size: 12px;

    h1 {
      margin-bottom: 24px !important;
      font-size: 18px !important;
    }

    h2,
    h3 {
      font-size: 14px !important;
    }

    .v-h2-big {
      font-size: 16px !important;
    }

    p {
      font-size: 12px !important;
    }
  }
}

.breadcrumbs-offset {
  margin-bottom: 20px;
}
</style>
